import { useState, useEffect, useCallback, useMemo } from "react";
import type { LangCode, Location, SeasonFilter } from "../data/types.js";
import { areas, DEFAULT_AREA_INDEX } from "../data/areas.js";
import { loadLang, saveLang, t } from "../i18n/strings.js";
import { ImageMap } from "./ImageMap.js";
import { LocationSheet } from "./LocationSheet.js";
import { FilterDropdown } from "./FilterDropdown.js";
import { LangSwitcher } from "./LangSwitcher.js";
import { AreaSwitcher } from "./AreaSwitcher.js";
import { PromoPill } from "./PromoPill.js";
import { WelcomeModal } from "./WelcomeModal.js";
import { InspirationGallery } from "./InspirationGallery.js";

function useLang() {
  const [lang, setLang] = useState<LangCode>(() => loadLang());
  const changeLang = useCallback((next: LangCode) => {
    setLang(next);
    saveLang(next);
  }, []);
  return { lang, changeLang };
}

export function AsakusaMap() {
  const [areaIndex, setAreaIndex] = useState(DEFAULT_AREA_INDEX);
  const [activeLocation, setActiveLocation] = useState<Location | null>(null);
  const [pinsByArea, setPinsByArea] = useState<Record<string, Location[]>>({});
  const [filter, setFilter] = useState<SeasonFilter>("all");
  const [inspirationOpen, setInspirationOpen] = useState(false);
  const { lang, changeLang } = useLang();

  const area = areas[areaIndex];
  const locations = pinsByArea[area.id] || area.fallbackLocations;

  /** Fetch pins + cloudinary photos for a given area, merge, then cache */
  const loadArea = useCallback(async (areaId: string) => {
    const target = areas.find((a) => a.id === areaId);
    if (!target) return;

    const [pinsRes, photosRes] = await Promise.allSettled([
      fetch(`/api/pins?area=${areaId}`).then((r) => r.json()),
      fetch(`/api/photos?area=${areaId}`).then((r) => r.json()),
    ]);

    let pins: Location[] = target.fallbackLocations;

    if (
      pinsRes.status === "fulfilled" &&
      Array.isArray(pinsRes.value) &&
      pinsRes.value.length > 0
    ) {
      pins = pinsRes.value as Location[];
    }

    if (
      photosRes.status === "fulfilled" &&
      photosRes.value &&
      !photosRes.value.error
    ) {
      const cloudPhotos = photosRes.value as Record<string, string[]>;
      pins = pins.map((pin) => {
        const folderPhotos = cloudPhotos[String(pin.number)];
        if (folderPhotos && folderPhotos.length > 0) {
          const hidden = new Set(((pin as Location & { hiddenPhotos?: string[] }).hiddenPhotos) || []);
          let urls = folderPhotos.filter((u: string) => !hidden.has(u));
          const order = (pin as Location & { photoOrder?: string[] }).photoOrder;
          if (order && order.length > 0) {
            const idx = new Map(order.map((u, i) => [u, i]));
            urls = [...urls].sort(
              (a, b) => (idx.get(a) ?? 999) - (idx.get(b) ?? 999)
            );
          }
          return {
            ...pin,
            photos: urls.map((url, i) => ({
              url,
              alt: `${pin.name || `Spot ${pin.number}`} — photo ${i + 1}`,
            })),
          };
        }
        return pin;
      });
    }

    setPinsByArea((prev) => ({ ...prev, [areaId]: pins }));
  }, []);

  useEffect(() => {
    areas.forEach((a) => loadArea(a.id));
  }, [loadArea]);

  const handleAreaChange = useCallback((i: number) => {
    setActiveLocation(null);
    setAreaIndex(i);
  }, []);

  const dimmedIds = useMemo(() => {
    if (filter === "all") return new Set<string>();
    if (filter === "night")
      return new Set(
        locations
          .filter((l) => !l.allowNight && !l.isShop)
          .map((l) => l.id)
      );
    if (filter === "sakura")
      return new Set(
        locations
          .filter((l) => !l.allowSakura && !l.isShop)
          .map((l) => l.id)
      );
    if (filter === "momiji")
      return new Set(
        locations
          .filter((l) => !l.allowMomiji && !l.isShop)
          .map((l) => l.id)
      );
    if (filter === "hot")
      return new Set(
        locations
          .filter((l) => !l.allowHot && !l.isShop)
          .map((l) => l.id)
      );
    return new Set<string>();
  }, [filter, locations]);

  const handlePinClick = useCallback((loc: Location) => {
    setActiveLocation((prev) => (prev?.id === loc.id ? null : loc));
  }, []);

  const handleClose = useCallback(() => setActiveLocation(null), []);

  return (
    <div className="fixed inset-0 bg-surface-1">
      <ImageMap
        mapImage={area.mapImage}
        activeLocation={activeLocation}
        onPinClick={handlePinClick}
        locations={locations}
        dimmedIds={dimmedIds}
      />
      <FilterDropdown filter={filter} onFilterChange={setFilter} lang={lang} />
      <LangSwitcher lang={lang} onChange={changeLang} />
      <AreaSwitcher areaIndex={areaIndex} onAreaChange={handleAreaChange} />
      <button
        onClick={() => setInspirationOpen(true)}
        className="fixed left-4 z-30 px-3.5 py-2 rounded-full bg-white/95 backdrop-blur shadow-lg text-[11px] font-semibold text-ink-0 active:scale-95 transition-transform"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 62px)" }}
        aria-label="Open inspiration gallery"
      >
        {t("inspiration.open", lang)}
      </button>
      <PromoPill hidden={!!activeLocation || inspirationOpen} lang={lang} />
      <LocationSheet
        location={activeLocation}
        onClose={handleClose}
        lang={lang}
      />
      <InspirationGallery
        open={inspirationOpen}
        locations={locations}
        lang={lang}
        onClose={() => setInspirationOpen(false)}
        onPickLocation={setActiveLocation}
      />
      <WelcomeModal lang={lang} />
    </div>
  );
}
